/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// hd44780-cgrom.js — the HD44780 character-generator font as PURE DATA (no
// DOM). Each glyph is a Uint8Array(8): one byte per dot-row, the low 5 bits
// being the 5 dot columns with bit 4 (0b10000) the LEFTMOST column. This is
// the same row/bit convention as the HD44780's CGRAM, so custom characters and
// ROM glyphs render through one path (`glyphRows`).
//
// Scope: the printable ASCII subset 0x20–0x7E (the A00 ROM matches ASCII here
// except for a few Japanese substitutions — ¥ at 0x5C, → / ← at 0x7E/0x7F —
// which we render as the ASCII glyphs for legibility). The kana/symbol banks
// (0xA0–0xFF) are not included; an unmapped code renders blank rather than
// crashing. Character codes 0x00–0x0F display the 8 CGRAM glyphs (0x08–0x0F
// alias 0x00–0x07), which come from run-volatile state, not this table.
//
// The 5×8 font's eighth row is the CURSOR's: every glyph keeps clear of it,
// descenders too — g, j, p, q, y and the comma sit a row high, their tails squeezed in, as
// the A00 table draws them (drawn on the eighth, an underline cursor ran
// into them).

/** Build a glyph from eight 5-bit row values. */
const g = (...rows) => Uint8Array.of(...rows);

const BLANK = g(0, 0, 0, 0, 0, 0, 0, 0);

/** code → Uint8Array(8) row bitmap. Frozen; missing codes fall back to BLANK. */
export const CGROM_A00 = Object.freeze({
  0x20: g(0, 0, 0, 0, 0, 0, 0, 0), // space
  0x21: g(0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b00000, 0b00100, 0), // !
  0x22: g(0b01010, 0b01010, 0b01010, 0b00000, 0b00000, 0b00000, 0b00000, 0), // "
  0x23: g(0b01010, 0b01010, 0b11111, 0b01010, 0b11111, 0b01010, 0b01010, 0), // #
  0x24: g(0b00100, 0b01111, 0b10100, 0b01110, 0b00101, 0b11110, 0b00100, 0), // $
  0x25: g(0b11000, 0b11001, 0b00010, 0b00100, 0b01000, 0b10011, 0b00011, 0), // %
  0x26: g(0b01100, 0b10010, 0b10100, 0b01000, 0b10101, 0b10010, 0b01101, 0), // &
  0x27: g(0b01100, 0b00100, 0b01000, 0b00000, 0b00000, 0b00000, 0b00000, 0), // '
  0x28: g(0b00010, 0b00100, 0b01000, 0b01000, 0b01000, 0b00100, 0b00010, 0), // (
  0x29: g(0b01000, 0b00100, 0b00010, 0b00010, 0b00010, 0b00100, 0b01000, 0), // )
  0x2a: g(0b00000, 0b00100, 0b10101, 0b01110, 0b10101, 0b00100, 0b00000, 0), // *
  0x2b: g(0b00000, 0b00100, 0b00100, 0b11111, 0b00100, 0b00100, 0b00000, 0), // +
  0x2c: g(0b00000, 0b00000, 0b00000, 0b00000, 0b01100, 0b00100, 0b01000, 0), // ,
  0x2d: g(0b00000, 0b00000, 0b00000, 0b11111, 0b00000, 0b00000, 0b00000, 0), // -
  0x2e: g(0b00000, 0b00000, 0b00000, 0b00000, 0b00000, 0b01100, 0b01100, 0), // .
  0x2f: g(0b00000, 0b00001, 0b00010, 0b00100, 0b01000, 0b10000, 0b00000, 0), // /
  0x30: g(0b01110, 0b10001, 0b10011, 0b10101, 0b11001, 0b10001, 0b01110, 0), // 0
  0x31: g(0b00100, 0b01100, 0b00100, 0b00100, 0b00100, 0b00100, 0b01110, 0), // 1
  0x32: g(0b01110, 0b10001, 0b00001, 0b00010, 0b00100, 0b01000, 0b11111, 0), // 2
  0x33: g(0b11111, 0b00010, 0b00100, 0b00010, 0b00001, 0b10001, 0b01110, 0), // 3
  0x34: g(0b00010, 0b00110, 0b01010, 0b10010, 0b11111, 0b00010, 0b00010, 0), // 4
  0x35: g(0b11111, 0b10000, 0b11110, 0b00001, 0b00001, 0b10001, 0b01110, 0), // 5
  0x36: g(0b00110, 0b01000, 0b10000, 0b11110, 0b10001, 0b10001, 0b01110, 0), // 6
  0x37: g(0b11111, 0b00001, 0b00010, 0b00100, 0b01000, 0b01000, 0b01000, 0), // 7
  0x38: g(0b01110, 0b10001, 0b10001, 0b01110, 0b10001, 0b10001, 0b01110, 0), // 8
  0x39: g(0b01110, 0b10001, 0b10001, 0b01111, 0b00001, 0b00010, 0b01100, 0), // 9
  0x3a: g(0b00000, 0b01100, 0b01100, 0b00000, 0b01100, 0b01100, 0b00000, 0), // :
  0x3b: g(0b00000, 0b01100, 0b01100, 0b00000, 0b01100, 0b00100, 0b01000, 0), // ;
  0x3c: g(0b00010, 0b00100, 0b01000, 0b10000, 0b01000, 0b00100, 0b00010, 0), // <
  0x3d: g(0b00000, 0b00000, 0b11111, 0b00000, 0b11111, 0b00000, 0b00000, 0), // =
  0x3e: g(0b01000, 0b00100, 0b00010, 0b00001, 0b00010, 0b00100, 0b01000, 0), // >
  0x3f: g(0b01110, 0b10001, 0b00001, 0b00010, 0b00100, 0b00000, 0b00100, 0), // ?
  0x40: g(0b01110, 0b10001, 0b00001, 0b01101, 0b10101, 0b10101, 0b01110, 0), // @
  0x41: g(0b01110, 0b10001, 0b10001, 0b11111, 0b10001, 0b10001, 0b10001, 0), // A
  0x42: g(0b11110, 0b10001, 0b10001, 0b11110, 0b10001, 0b10001, 0b11110, 0), // B
  0x43: g(0b01110, 0b10001, 0b10000, 0b10000, 0b10000, 0b10001, 0b01110, 0), // C
  0x44: g(0b11100, 0b10010, 0b10001, 0b10001, 0b10001, 0b10010, 0b11100, 0), // D
  0x45: g(0b11111, 0b10000, 0b10000, 0b11110, 0b10000, 0b10000, 0b11111, 0), // E
  0x46: g(0b11111, 0b10000, 0b10000, 0b11110, 0b10000, 0b10000, 0b10000, 0), // F
  0x47: g(0b01110, 0b10001, 0b10000, 0b10111, 0b10001, 0b10001, 0b01111, 0), // G
  0x48: g(0b10001, 0b10001, 0b10001, 0b11111, 0b10001, 0b10001, 0b10001, 0), // H
  0x49: g(0b01110, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b01110, 0), // I
  0x4a: g(0b00111, 0b00010, 0b00010, 0b00010, 0b00010, 0b10010, 0b01100, 0), // J
  0x4b: g(0b10001, 0b10010, 0b10100, 0b11000, 0b10100, 0b10010, 0b10001, 0), // K
  0x4c: g(0b10000, 0b10000, 0b10000, 0b10000, 0b10000, 0b10000, 0b11111, 0), // L
  0x4d: g(0b10001, 0b11011, 0b10101, 0b10101, 0b10001, 0b10001, 0b10001, 0), // M
  0x4e: g(0b10001, 0b10001, 0b11001, 0b10101, 0b10011, 0b10001, 0b10001, 0), // N
  0x4f: g(0b01110, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01110, 0), // O
  0x50: g(0b11110, 0b10001, 0b10001, 0b11110, 0b10000, 0b10000, 0b10000, 0), // P
  0x51: g(0b01110, 0b10001, 0b10001, 0b10001, 0b10101, 0b10010, 0b01101, 0), // Q
  0x52: g(0b11110, 0b10001, 0b10001, 0b11110, 0b10100, 0b10010, 0b10001, 0), // R
  0x53: g(0b01111, 0b10000, 0b10000, 0b01110, 0b00001, 0b00001, 0b11110, 0), // S
  0x54: g(0b11111, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0), // T
  0x55: g(0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01110, 0), // U
  0x56: g(0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01010, 0b00100, 0), // V
  0x57: g(0b10001, 0b10001, 0b10001, 0b10101, 0b10101, 0b10101, 0b01010, 0), // W
  0x58: g(0b10001, 0b10001, 0b01010, 0b00100, 0b01010, 0b10001, 0b10001, 0), // X
  0x59: g(0b10001, 0b10001, 0b10001, 0b01010, 0b00100, 0b00100, 0b00100, 0), // Y
  0x5a: g(0b11111, 0b00001, 0b00010, 0b00100, 0b01000, 0b10000, 0b11111, 0), // Z
  0x5b: g(0b01110, 0b01000, 0b01000, 0b01000, 0b01000, 0b01000, 0b01110, 0), // [
  0x5c: g(0b00000, 0b10000, 0b01000, 0b00100, 0b00010, 0b00001, 0b00000, 0), // backslash
  0x5d: g(0b01110, 0b00010, 0b00010, 0b00010, 0b00010, 0b00010, 0b01110, 0), // ]
  0x5e: g(0b00100, 0b01010, 0b10001, 0b00000, 0b00000, 0b00000, 0b00000, 0), // ^
  0x5f: g(0b00000, 0b00000, 0b00000, 0b00000, 0b00000, 0b00000, 0b11111, 0), // _
  0x60: g(0b01000, 0b00100, 0b00010, 0b00000, 0b00000, 0b00000, 0b00000, 0), // `
  0x61: g(0b00000, 0b00000, 0b01110, 0b00001, 0b01111, 0b10001, 0b01111, 0), // a
  0x62: g(0b10000, 0b10000, 0b10110, 0b11001, 0b10001, 0b10001, 0b11110, 0), // b
  0x63: g(0b00000, 0b00000, 0b01110, 0b10000, 0b10000, 0b10001, 0b01110, 0), // c
  0x64: g(0b00001, 0b00001, 0b01101, 0b10011, 0b10001, 0b10001, 0b01111, 0), // d
  0x65: g(0b00000, 0b00000, 0b01110, 0b10001, 0b11111, 0b10000, 0b01110, 0), // e
  0x66: g(0b00110, 0b01001, 0b01000, 0b11100, 0b01000, 0b01000, 0b01000, 0), // f
  0x67: g(0b00000, 0b00000, 0b01111, 0b10001, 0b01111, 0b00001, 0b01110, 0), // g
  0x68: g(0b10000, 0b10000, 0b10110, 0b11001, 0b10001, 0b10001, 0b10001, 0), // h
  0x69: g(0b00100, 0b00000, 0b01100, 0b00100, 0b00100, 0b00100, 0b01110, 0), // i
  0x6a: g(0b00010, 0b00000, 0b00110, 0b00010, 0b00010, 0b10010, 0b01100, 0), // j
  0x6b: g(0b10000, 0b10000, 0b10010, 0b10100, 0b11000, 0b10100, 0b10010, 0), // k
  0x6c: g(0b01100, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b01110, 0), // l
  0x6d: g(0b00000, 0b00000, 0b11010, 0b10101, 0b10101, 0b10001, 0b10001, 0), // m
  0x6e: g(0b00000, 0b00000, 0b10110, 0b11001, 0b10001, 0b10001, 0b10001, 0), // n
  0x6f: g(0b00000, 0b00000, 0b01110, 0b10001, 0b10001, 0b10001, 0b01110, 0), // o
  0x70: g(0b00000, 0b00000, 0b11110, 0b10001, 0b11110, 0b10000, 0b10000, 0), // p
  0x71: g(0b00000, 0b00000, 0b01101, 0b10011, 0b01111, 0b00001, 0b00001, 0), // q
  0x72: g(0b00000, 0b00000, 0b10110, 0b11001, 0b10000, 0b10000, 0b10000, 0), // r
  0x73: g(0b00000, 0b00000, 0b01111, 0b10000, 0b01110, 0b00001, 0b11110, 0), // s
  0x74: g(0b01000, 0b01000, 0b11100, 0b01000, 0b01000, 0b01001, 0b00110, 0), // t
  0x75: g(0b00000, 0b00000, 0b10001, 0b10001, 0b10001, 0b10011, 0b01101, 0), // u
  0x76: g(0b00000, 0b00000, 0b10001, 0b10001, 0b10001, 0b01010, 0b00100, 0), // v
  0x77: g(0b00000, 0b00000, 0b10001, 0b10001, 0b10101, 0b10101, 0b01010, 0), // w
  0x78: g(0b00000, 0b00000, 0b10001, 0b01010, 0b00100, 0b01010, 0b10001, 0), // x
  0x79: g(0b00000, 0b00000, 0b10001, 0b10001, 0b01111, 0b00001, 0b01110, 0), // y
  0x7a: g(0b00000, 0b00000, 0b11111, 0b00010, 0b00100, 0b01000, 0b11111, 0), // z
  0x7b: g(0b00010, 0b00100, 0b00100, 0b01000, 0b00100, 0b00100, 0b00010, 0), // {
  0x7c: g(0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0), // |
  0x7d: g(0b01000, 0b00100, 0b00100, 0b00010, 0b00100, 0b00100, 0b01000, 0), // }
  0x7e: g(0b00000, 0b00000, 0b01000, 0b10101, 0b00010, 0b00000, 0b00000, 0), // ~
});

/**
 * The 8 dot-rows for a character code. Codes 0x00–0x0F show the 8 CGRAM glyphs
 * (0x08–0x0F alias 0x00–0x07); everything else comes from the ROM table, with
 * an unmapped code rendering blank.
 * @param {number} code   0–255 DDRAM byte
 * @param {Uint8Array} [cgram]  64-byte custom-glyph RAM (8 glyphs × 8 rows)
 * @returns {Uint8Array} eight 5-bit rows
 */
export function glyphRows(code, cgram) {
  if (code < 0x10) {
    const base = (code & 0x07) * 8;
    if (cgram && cgram.length >= base + 8)
      return cgram.subarray(base, base + 8);
    return BLANK;
  }
  return CGROM_A00[code] ?? BLANK;
}
